import React, { useState, useEffect, useRef, useCallback } from 'react';
import FileUploader from './FileUploader';
import Welcome from './Welcome'; // Import the Welcome component
import { db, auth } from '../firebaseConfig'; // Import Firestore and Auth from firebaseConfig
import { loadBookmarks, addBookmark, deleteBookmark, updateBookmark } from '../bookmarkService'; // Import Firestore functions
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged } from 'firebase/auth'; // Import necessary Firebase Auth methods
import { checkUrl, checkUrls } from '../utils/urlChecker';

const TagInput = ({ tags, onChange, existingTags = [], placeholder, onKeyDown }) => {
    const [inputValue, setInputValue] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    // 获取所有现有标签的建议
    const getSuggestions = (value) => {
        const inputValue = value.toLowerCase();
        const inputLength = inputValue.length;
        
        return inputLength === 0 ? [] : existingTags.filter(tag =>
            tag.toLowerCase().includes(inputValue) &&
            !tags.includes(tag)
        );
    };

    // 处理输入变化
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setSuggestions(getSuggestions(value));
        setShowSuggestions(true);
        setSelectedIndex(-1);
    };

    // 处理标签选择
    const handleSelectTag = (tag) => {
        onChange([...tags, tag]);
        setInputValue('');
        setShowSuggestions(false);
        setSelectedIndex(-1);
    };

    // 修改键盘事件处理
    const handleKeyDown = (e) => {
        // 如果提供了自定义的 onKeyDown 处理函数，先执行它
        if (onKeyDown) {
            onKeyDown(e);
        }

        // 如果事件已经被阻止，直接返回
        if (e.defaultPrevented) {
            return;
        }

        if (showSuggestions && suggestions.length > 0) {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev => 
                        prev < suggestions.length - 1 ? prev + 1 : prev
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (selectedIndex >= 0) {
                        handleSelectTag(suggestions[selectedIndex]);
                    } else if (inputValue.trim()) {
                        handleSelectTag(inputValue.trim());
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    setShowSuggestions(false);
                    setSelectedIndex(-1);
                    break;
                default:
                    break;
            }
        } else if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            handleSelectTag(inputValue.trim());
        }

        // 处理 Backspace 删除标签
        if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
            e.preventDefault();
            const newTags = tags.slice(0, -1);
            onChange(newTags);
        }
    };

    // 点击外部关闭建议列表
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const removeTag = (indexToRemove) => {
        const newTags = tags.filter((_, index) => index !== indexToRemove);
        onChange(newTags);
    };

    return (
        <div className="relative">
            <div className="flex flex-wrap gap-2 p-1 border border-indigo-200 rounded bg-white min-h-[42px]">
                {tags.map((tag, index) => (
                    <span
                        key={index}
                        className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full flex items-center text-sm"
                    >
                        {tag}
                        <button
                            type="button"
                            onClick={() => removeTag(index)}
                            className="w-5 h-5 ml-1 inline-flex items-center justify-center rounded-md bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                        >
                            <i className="fas fa-times text-xs"></i>
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setShowSuggestions(true)}
                    className="flex-1 outline-none min-w-[60px] p-1"
                    placeholder={placeholder || (tags.length === 0 ? "Type and press Enter to add tags" : "")}
                />
            </div>
            {showSuggestions && suggestions.length > 0 && (
                <div 
                    ref={suggestionsRef}
                    className="absolute z-10 mt-1 w-full bg-white border border-indigo-200 shadow-lg max-h-60 rounded-md overflow-auto focus:outline-none sm:text-sm"
                >
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 
                                ${index === selectedIndex ? 'bg-indigo-100' : 'hover:bg-indigo-50'} 
                                text-sm transition-colors`}
                            onClick={() => handleSelectTag(suggestion)}
                        >
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// 修改 TagDisplay 组件
const TagDisplay = ({ tags, onTagClick }) => {
    return (
        <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
                <button
                    key={index}
                    onClick={() => onTagClick(tag)}
                    className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-sm hover:bg-indigo-200 transition-colors cursor-pointer"
                >
                    {tag}
                </button>
            ))}
        </div>
    );
};

// 提取成独立组件
const TagEditorDialog = ({ onClose, onUpdate, existingTags, initialOldTag = '', initialNewTag = '' }) => {
    const [formState, setFormState] = useState({
        oldTag: initialOldTag,
        newTag: initialNewTag
    });

    const handleSubmit = () => {
        onUpdate(formState.oldTag, formState.newTag);
    };

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 relative">
                <h3 className="text-lg font-medium mb-4">Edit Tag</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Old Tag</label>
                        <select
                            value={formState.oldTag}
                            onChange={(e) => setFormState(prev => ({ ...prev, oldTag: e.target.value }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">Select a tag</option>
                            {existingTags.map(tag => (
                                <option key={tag} value={tag}>{tag}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Tag</label>
                        <input
                            type="text"
                            value={formState.newTag}
                            onChange={(e) => setFormState(prev => ({ ...prev, newTag: e.target.value }))}
                            className="block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Enter new tag name"
                            autoFocus
                        />
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!formState.oldTag || !formState.newTag}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Update
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Table = ({ searchTags, onSearch, onTagsUpdate }) => {
    // 获取今天的日期字符串 (YYYY-MM-DD 格式)
    const today = new Date().toISOString().split('T')[0];
    
    const [data, setData] = useState([]); // Local state for bookmarks
    const [deletedBookmarks, setDeletedBookmarks] = useState([]); // Local state for deleted bookmarks
    const [error, setError] = useState('');
    const [newBookmark, setNewBookmark] = useState({ 
        title: '', 
        url: '', 
        tags: [], 
        date: today 
    }); // State for new bookmark
    const [docIds, setDocIds] = useState([]); // State to store document IDs
    const [syncProgress, setSyncProgress] = useState(null); // 添加同步进度状态
    const [editingBookmark, setEditingBookmark] = useState(null); // 添加编辑状态
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });
    const [modifiedBookmarks, setModifiedBookmarks] = useState(new Set()); // 跟踪修改的书签
    const [isSyncing, setIsSyncing] = useState(false); // 添加同步状态
    const [urlStatus, setUrlStatus] = useState({}); // 存储URL的可访问状态
    const [isChecking, setIsChecking] = useState(false); // 检查状态
    const [showTagEditor, setShowTagEditor] = useState(false);
    const [tagEditorConfig, setTagEditorConfig] = useState({ oldTag: '', newTag: '' });

    // Check user authentication state on component mount
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (currentUser) {
                loadUserBookmarks(currentUser);
            }
        });

        return () => unsubscribe();
    }, []);

    const loadUserBookmarks = async (currentUser) => {
        try {
            const bookmarks = await loadBookmarks(currentUser);
            setData(bookmarks);
            setDocIds(bookmarks.map(bookmark => bookmark.id));
            setError(''); // 清除任何可能存在的错误消息
            console.log("Bookmarks loaded:", bookmarks);
        } catch (err) {
            console.error("Error loading bookmarks: ", err);
            setError("Failed to load bookmarks.");
        }
    };

    const handleFileParsed = (bookmarks) => {
        setData(bookmarks);
        setError('');
    };

    const handleEdit = (bookmark) => {
        setEditingBookmark({
            ...bookmark,
            tags: Array.isArray(bookmark.tags) ? bookmark.tags : [], // 保持tags作为数组
            date: bookmark.date || today
        });
    };

    const handleCancelEdit = () => {
        setEditingBookmark(null);
    };

    const handleSaveEdit = async () => {
        try {
            const updatedBookmark = {
                ...editingBookmark,
                tags: Array.isArray(editingBookmark.tags) ? editingBookmark.tags : [],
                creatorId: auth.currentUser.uid
            };
            
            // 添加到修改集合
            setModifiedBookmarks(prev => new Set(prev).add(editingBookmark.id));
            
            // 更新本地数据
            setData(data.map(item => 
                item.id === editingBookmark.id ? updatedBookmark : item
            ));
            
            setEditingBookmark(null);
        } catch (error) {
            console.error("Error updating bookmark:", error);
            setError("Failed to update bookmark");
        }
    };

    const handleDelete = (id) => {
        // Find the bookmark to delete
        const bookmarkToDelete = data.find(bookmark => bookmark.id === id);
        if (bookmarkToDelete) {
            // Add to deleted bookmarks array for syncing later
            setDeletedBookmarks([...deletedBookmarks, bookmarkToDelete]);
            // Remove from main data array
            setData(data.filter(bookmark => bookmark.id !== id));
            console.log(`Bookmark with ID ${id} marked for deletion.`);
        }
    };

    const handleRestore = (id) => {
        // Find the bookmark to restore
        const bookmarkToRestore = deletedBookmarks.find(bookmark => bookmark.id === id);
        if (bookmarkToRestore) {
            setData([...data, bookmarkToRestore]); // Restore the bookmark to the main list
            setDeletedBookmarks(deletedBookmarks.filter(bookmark => bookmark.id !== id)); // Remove from deleted bookmarks
            console.log(`Bookmark with ID ${id} restored.`);
        }
    };

    const handleDeduplicate = () => {
        const uniqueBookmarks = {};
        
        data.forEach((bookmark) => {
            if (uniqueBookmarks[bookmark.url]) {
                uniqueBookmarks[bookmark.url].tags = [
                    ...new Set([...uniqueBookmarks[bookmark.url].tags, ...bookmark.tags])
                ];
            } else {
                uniqueBookmarks[bookmark.url] = { ...bookmark };
            }
        });

        setData(Object.values(uniqueBookmarks));
    };

    const handleSync = async () => {
        if (isSyncing) return; // 如果正在同步，直接返回
        
        setIsSyncing(true); // 开始同步
        try {
            const currentUser = auth.currentUser;
            
            // 计算需要同步的总数
            const totalOperations = deletedBookmarks.length + 
                data.filter(item => !item.id || modifiedBookmarks.has(item.id)).length;
            
            let completedOperations = 0;
            
            // 处理删除的书签
            const remainingDeletedBookmarks = [];
            for (const deletedBookmark of deletedBookmarks) {
                if (deletedBookmark.id) {
                    try {
                        await deleteBookmark(deletedBookmark.id);
                        completedOperations++;
                        setSyncProgress(`${completedOperations}/${totalOperations}`);
                        console.log(`Bookmark with ID ${deletedBookmark.id} deleted from Firestore.`);
                    } catch (error) {
                        console.error(`Error deleting bookmark ${deletedBookmark.id}:`, error);
                        remainingDeletedBookmarks.push(deletedBookmark);
                    }
                }
            }

            setDeletedBookmarks(remainingDeletedBookmarks);

            // 处理新增和修改的书签
            for (const bookmark of data) {
                // 只处理新增（没有id）或已修改的书签
                if (!bookmark.id || modifiedBookmarks.has(bookmark.id)) {
                    try {
                        const bookmarkWithCreatorId = {
                            ...bookmark,
                            creatorId: currentUser.uid
                        };

                        if (bookmark.id) {
                            // 更新已存在的书签
                            await updateBookmark(bookmark.id, bookmarkWithCreatorId);
                            console.log(`Bookmark updated with ID: ${bookmark.id}`);
                        } else {
                            // 添加新书签
                            const newBookmark = await addBookmark(bookmarkWithCreatorId);
                            setDocIds(prevIds => [...prevIds, newBookmark.id]);
                            // 更新本地数据中的ID
                            setData(prevData => prevData.map(item => 
                                item === bookmark ? { ...item, id: newBookmark.id } : item
                            ));
                            console.log(`Bookmark added with ID: ${newBookmark.id}`);
                        }
                        completedOperations++;
                        setSyncProgress(`${completedOperations}/${totalOperations}`);
                    } catch (error) {
                        console.error(`Error syncing bookmark:`, error);
                        setError(`Failed to sync bookmark: ${bookmark.title}`);
                    }
                }
            }

            // 清除修改记录
            setModifiedBookmarks(new Set());
            
            console.log("Bookmarks synced to Firestore!");
            setTimeout(() => {
                setSyncProgress(null);
                setIsSyncing(false); // 同步完成
            }, 2000);
        } catch (error) {
            console.error("Error syncing bookmarks: ", error);
            setError("Failed to sync bookmarks.");
            setSyncProgress(null);
            setIsSyncing(false); // 同步出错时也要重置状态
        }
    };

    const handleAddBookmark = async (e) => {
        e.preventDefault();
        const newBookmarkData = {
            ...newBookmark,
            creatorId: auth.currentUser.uid,
            tags: newBookmark.tags,
            createdAt: new Date().toISOString(),
        };
        
        // 添加到本地数据，不立即同步到服务器
        setData([...data, newBookmarkData]);
        setNewBookmark({ title: '', url: '', tags: [], date: today });
        console.log("New bookmark added locally!");
    };

    // 修改搜索过滤函数
    const filteredData = data.filter(item => {
        if (searchTags.length === 0) return true;
        
        return searchTags.every(searchTag => {
            const searchLower = searchTag.toLowerCase();
            return (
                item.title.toLowerCase().includes(searchLower) ||
                item.url.toLowerCase().includes(searchLower) ||
                item.tags.some(tag => tag.toLowerCase().includes(searchLower))
            );
        });
    });

    // 排序处理函数
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // 获取排序图标
    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="fas fa-sort text-gray-300 ml-1"></i>;
        }
        return sortConfig.direction === 'asc' 
            ? <i className="fas fa-sort-up text-indigo-600 ml-1"></i>
            : <i className="fas fa-sort-down text-indigo-600 ml-1"></i>;
    };

    // 排序数据
    const sortedData = React.useMemo(() => {
        let sortableItems = [...filteredData];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                
                // 特殊处理 tags 数组
                if (sortConfig.key === 'tags') {
                    aValue = a.tags.join(', ');
                    bValue = b.tags.join(', ');
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredData, sortConfig]);

    // 计算变更统计
    const getChangeStats = () => {
        const newCount = data.filter(item => !item.id).length;
        const modifiedCount = modifiedBookmarks.size;
        const deletedCount = deletedBookmarks.length;
        
        const stats = [];
        if (newCount > 0) stats.push(`${newCount} new`);
        if (modifiedCount > 0) stats.push(`${modifiedCount} modified`);
        if (deletedCount > 0) stats.push(`${deletedCount} deleted`);
        
        return stats.length > 0 ? stats.join(', ') : null;
    };

    // 修改检查链接的函数
    const handleCheckUrls = async () => {
        if (isChecking) return;
        setIsChecking(true);
        
        try {
            const urls = sortedData.map(item => item.url);
            await checkUrls(
                urls, 
                (checked, total) => {
                    setSyncProgress(`Checking: ${checked}/${total}`);
                },
                // 添加状态更新回调
                (url, result) => {
                    setUrlStatus(prevStatus => ({
                        ...prevStatus,
                        [url]: result
                    }));
                }
            );
        } catch (error) {
            console.error('Error checking URLs:', error);
            setError('Failed to check URLs');
        } finally {
            setIsChecking(false);
            setSyncProgress(null);
        }
    };

    // 修改单个 URL 检查函数
    const handleCheckSingleUrl = async (url) => {
        try {
            const result = await checkUrl(url);
            setUrlStatus(prevStatus => ({
                ...prevStatus,
                [url]: result
            }));
        } catch (error) {
            console.error('Error checking URL:', error);
        }
    };

    // URL 状态指示器组件
    const UrlStatusIndicator = ({ url }) => {
        const status = urlStatus[url];
        console.log(url, status);
        // 如果没有状态，返回空的占位符，保持布局一致
        if (!status) {
            return <span className="inline-block w-2 h-2 rounded-full mr-2" />;
        }
        
        let className = 'inline-block w-2 h-2 rounded-full mr-2 ';
        let title = '';
        
        if (!status.isAccessible) {
            className += 'bg-red-500';
            title = 'Not accessible';
        } else if (status.isRedirect) {
            className += 'bg-yellow-500';
            title = 'Redirects to another URL';
        } else {
            className += 'bg-green-500';
            title = 'Accessible';
        }
        
        return (
            <span 
                className={className}
                title={title}
            />
        );
    };

    // 添加标签点击处理函数
    const handleTagClick = (tag) => {
        onSearch(tag);
    };

    // 修改 getAllTags 函数，只返回标签，不触发更新
    const getAllTags = () => {
        const tagSet = new Set();
        data.forEach(bookmark => {
            bookmark.tags.forEach(tag => tagSet.add(tag));
        });
        return Array.from(tagSet);
    };

    // 只在 data 变化时更新标签列表
    useEffect(() => {
        // 只有当 data 有内容时才更新标签
        if (data.length > 0) {
            const tags = getAllTags();
            onTagsUpdate?.(tags);
        }
    }, [data]); // 只依赖 data

    // 使用 useCallback 优化回调函数
    const handleTagRename = useCallback((oldTag, newTag) => {
        const updatedData = data.map(bookmark => ({
            ...bookmark,
            tags: bookmark.tags.map(tag => tag === oldTag ? newTag : tag)
        }));

        data.forEach(bookmark => {
            if (bookmark.tags.includes(oldTag)) {
                setModifiedBookmarks(prev => new Set(prev).add(bookmark.id));
            }
        });

        setData(updatedData);
        setShowTagEditor(false);
    }, [data]);

    return (
        <div className="overflow-x-auto">
            <div className="flex justify-between items-center mb-4">
                <FileUploader onFileParsed={handleFileParsed} />
                <div className="flex items-center gap-4">
                    {error && (
                        <div className="flex items-center bg-rose-50 text-rose-600 px-3 py-1 rounded-md">
                            <i className="fas fa-exclamation-circle mr-2"></i>
                            <span>{error}</span>
                        </div>
                    )}
                    {syncProgress && (
                        <div className="flex items-center bg-indigo-50 text-indigo-600 px-3 py-1 rounded-md">
                            <i className="fas fa-sync-alt animate-spin mr-2"></i>
                            <span>{syncProgress}</span>
                        </div>
                    )}
                    {getChangeStats() && (
                        <div className="flex items-center gap-2">
                            {data.filter(item => !item.id).length > 0 && (
                                <span className="bg-emerald-50 text-emerald-600 px-3 py-1 rounded-md flex items-center">
                                    <i className="fas fa-plus-circle mr-2"></i>
                                    {data.filter(item => !item.id).length} new
                                </span>
                            )}
                            {modifiedBookmarks.size > 0 && (
                                <span className="bg-amber-50 text-amber-600 px-3 py-1 rounded-md flex items-center">
                                    <i className="fas fa-edit mr-2"></i>
                                    {modifiedBookmarks.size} modified
                                </span>
                            )}
                            {deletedBookmarks.length > 0 && (
                                <span className="bg-rose-50 text-rose-600 px-3 py-1 rounded-md flex items-center">
                                    <i className="fas fa-trash mr-2"></i>
                                    {deletedBookmarks.length} deleted
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center bg-indigo-50 text-indigo-600 px-3 py-1 rounded-md">
                        <i className="fas fa-bookmark mr-2"></i>
                        Total: {filteredData.length}
                    </div>
                    <button 
                        onClick={handleDeduplicate} 
                        className="bg-emerald-600 text-white rounded px-4 py-2 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSyncing || isChecking}>
                        Deduplicate
                    </button>
                    <button 
                        onClick={handleCheckUrls}
                        disabled={isSyncing || isChecking}
                        className="bg-yellow-600 text-white rounded px-4 py-2 hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Check URL accessibility:
• Green: URL is directly accessible (200)
• Yellow: URL redirects to another location (301/302)
• Red: URL is not accessible
• No indicator: Not checked yet"
                    >
                        {isChecking ? 'Checking...' : 'Check URLs'}
                    </button>
                    <button 
                        onClick={handleSync} 
                        disabled={isSyncing || isChecking}
                        className="bg-indigo-600 text-white rounded px-4 py-2 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isSyncing ? 'Syncing...' : 'Sync'}
                    </button>
                    <button 
                        onClick={() => setShowTagEditor(true)}
                        className="bg-indigo-600 text-white rounded px-4 py-2 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isSyncing || isChecking}
                    >
                        Edit Tags
                    </button>
                </div>
            </div>

            {showTagEditor && (
                <TagEditorDialog
                    onClose={() => setShowTagEditor(false)}
                    onUpdate={handleTagRename}
                    existingTags={getAllTags()}
                />
            )}

            <table className="min-w-full divide-y divide-gray-200">
                <thead>
                    <tr>
                        <th 
                            scope="col" 
                            className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleSort('url')}
                        >
                            <div className="flex items-center">
                                URL
                                {getSortIcon('url')}
                            </div>
                        </th>
                        <th 
                            scope="col" 
                            className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleSort('title')}
                        >
                            <div className="flex items-center">
                                Title
                                {getSortIcon('title')}
                            </div>
                        </th>
                        <th 
                            scope="col" 
                            className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleSort('tags')}
                        >
                            <div className="flex items-center">
                                Tags
                                {getSortIcon('tags')}
                            </div>
                        </th>
                        <th 
                            scope="col" 
                            className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                            onClick={() => handleSort('date')}
                        >
                            <div className="flex items-center">
                                Date
                                {getSortIcon('date')}
                            </div>
                        </th>
                        <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    <tr className="bg-gray-50/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                            <input 
                                type="text" 
                                placeholder="Enter URL" 
                                value={newBookmark.url} 
                                onChange={(e) => setNewBookmark({ ...newBookmark, url: e.target.value })} 
                                required 
                                className="w-full border border-gray-300 rounded-md shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <input 
                                type="text" 
                                placeholder="Enter title" 
                                value={newBookmark.title} 
                                onChange={(e) => setNewBookmark({ ...newBookmark, title: e.target.value })} 
                                required 
                                className="w-full border border-gray-300 rounded-md shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <TagInput
                                tags={Array.isArray(newBookmark.tags) ? newBookmark.tags : []}
                                onChange={(newTags) => setNewBookmark({
                                    ...newBookmark,
                                    tags: newTags
                                })}
                                existingTags={getAllTags()}
                            />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <input 
                                type="date" 
                                value={newBookmark.date} 
                                onChange={(e) => setNewBookmark({ ...newBookmark, date: e.target.value })} 
                                className="w-full border border-gray-300 rounded-md shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <button 
                                onClick={handleAddBookmark}
                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors"
                            >
                                <i className="fas fa-plus"></i>
                            </button>
                        </td>
                    </tr>

                    {sortedData.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors duration-200">
                            {editingBookmark?.id === item.id ? (
                                // 编辑模式
                                <>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="text"
                                            value={editingBookmark.url}
                                            onChange={(e) => setEditingBookmark({
                                                ...editingBookmark,
                                                url: e.target.value
                                            })}
                                            className="w-full border border-gray-300 rounded-md shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="text"
                                            value={editingBookmark.title}
                                            onChange={(e) => setEditingBookmark({
                                                ...editingBookmark,
                                                title: e.target.value
                                            })}
                                            className="w-full border border-gray-300 rounded-md shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <TagInput
                                            tags={Array.isArray(editingBookmark.tags) ? editingBookmark.tags : []}
                                            onChange={(newTags) => setEditingBookmark({
                                                ...editingBookmark,
                                                tags: newTags
                                            })}
                                            existingTags={getAllTags()}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <input
                                            type="date"
                                            value={editingBookmark.date}
                                            onChange={(e) => setEditingBookmark({
                                                ...editingBookmark,
                                                date: e.target.value
                                            })}
                                            className="w-full border border-gray-300 rounded-md shadow-sm px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex justify-start gap-3">
                                            <button 
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-colors" 
                                                onClick={handleSaveEdit}
                                                title="Save">
                                                <i className="fas fa-check"></i>
                                            </button>
                                            <button 
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-gray-50 text-gray-600 hover:bg-gray-600 hover:text-white transition-colors" 
                                                onClick={handleCancelEdit}
                                                title="Cancel">
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                    </td>
                                </>
                            ) : (
                                // 显示模式
                                <>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center">
                                            <UrlStatusIndicator url={item.url} />
                                            <div className="truncate max-w-xs">
                                                <a 
                                                    href={item.url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    {item.url}
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-xs" title={item.title}>
                                        {item.title}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <TagDisplay 
                                            tags={item.tags} 
                                            onTagClick={handleTagClick}
                                        />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {item.date}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex justify-start gap-3">
                                            <button 
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-yellow-50 text-yellow-600 hover:bg-yellow-600 hover:text-white transition-colors" 
                                                onClick={() => handleCheckSingleUrl(item.url)}
                                                title="Check URL">
                                                <i className="fas fa-link"></i>
                                            </button>
                                            <button 
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors" 
                                                onClick={() => handleEdit(item)}
                                                title="Edit">
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button 
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-colors" 
                                                onClick={() => handleDelete(item.id)}
                                                title="Delete">
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export { TagInput };
export default Table; 